import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Target } from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../misc/Card";
import { Button } from "../../misc/Button";
import CustomToast from "../../misc/CustomToast";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { getApiErrorMessage } from "../../../utils/apiError";
import { cn } from "../../../utils/classMerger";
import InteractiveAiUsageBar from "../../niche/InteractiveAiUsageBar";
import NicheProfileForm from "../../niche/NicheProfileForm";
import NicheRefreshNote from "../../niche/NicheRefreshNote";
import NicheUpsell from "../../niche/NicheUpsell";
import { nicheErrorCode, nicheErrorExtra, saveNicheProfile } from "../../niche/nicheApi";
import { EMPTY_PROFILE, NicheProfile } from "../../niche/types";
import { useNiche } from "../../niche/useNiche";

const MyNicheSettings: React.FC = () => {
    const { overview, loading, error, refresh } = useNiche();
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);
    const [errors, setErrors] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);

    const save = async (profile: NicheProfile): Promise<void> => {
        setBusy(true);
        setErrors([]);
        try {
            await saveNicheProfile(profile);
            await refresh();
            toast.success(<CustomToast message="Your niche is saved." type="success" />);
        } catch (e) {
            const code = nicheErrorCode(e);
            if (code === "NICHE_PROFILE_INVALID") setErrors(nicheErrorExtra<string[]>(e, "errors") ?? []);
            else toast.error(<CustomToast message={getApiErrorMessage(e, "Couldn't save your niche. Try again.")} type="error" />);
            // Lost AI Pro since the page loaded: reload, so the section shows the upsell.
            if (code === "NICHE_NOT_ELIGIBLE") await refresh();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className={cn("w-full max-w-4xl mx-auto", isMobile ? "h-full m-0 rounded-none" : "h-auto")}>
            <div className="p-4">
                <Button onClick={() => navigate("/settings")} className="mb-4 flex items-center">
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            <CardHeader>
                <CardTitle className="flex items-center"><Target className="h-6 w-6 mr-2" /> My Niche</CardTitle>
                <CardDescription>Who you serve, what you know best, and where. It powers My Niche Video ideas.</CardDescription>
            </CardHeader>
            {loading && <CardContent><p className="text-sm text-gray-500">Loading…</p></CardContent>}
            {!loading && !overview && (
                <CardContent>
                    <p className="flex items-center gap-2 text-sm text-gray-600">
                        Something went wrong.{" "}
                        <Button type="button" size="sm" variant="outline" onClick={() => void refresh()}>Try again</Button>
                    </p>
                </CardContent>
            )}
            {!loading && overview && error && <NicheRefreshNote onRetry={() => void refresh()} />}
            {!loading && overview && !overview.eligible && <NicheUpsell />}
            {!loading && overview?.eligible && (
                <CardContent className="space-y-6 pb-8">
                    <InteractiveAiUsageBar allowance={overview.allowance} />
                    <p className="text-sm">
                        Not sure how to put it? <Link to="/my-niche" className="underline">Take the guided interview</Link>.
                    </p>
                    <NicheProfileForm
                        key={overview.profile?.savedAt ?? "empty"}
                        initial={overview.profile ?? EMPTY_PROFILE}
                        submitLabel="Save my niche"
                        onSubmit={save}
                        errors={errors}
                        busy={busy}
                    />
                </CardContent>
            )}
        </Card>
    );
};

export default MyNicheSettings;
